import type { FormGroup, ValidatorFn } from '@angular/forms';
import type { FormQuestion } from '@freelancer/step-logic-manager';
import { ValidationTypeApi } from '@freelancer/step-logic-manager';
import type {
  SelectAcceptedType,
  SelectGroups,
  SelectItem,
} from '@freelancer/ui/select';
import {
  currentOrFutureDate,
  dateFormat,
  maxLength,
  maxValue,
  minLength,
  minValue,
  notPattern,
  pattern,
  required,
} from '@freelancer/ui/validators';
import type { CustomInput } from './form-custom-inputs.types';

export function setValidators(input: CustomInput): void {
  if (!input.validations) {
    return;
  }

  const validators: ValidatorFn[] = [];

  input.validations?.forEach(validator => {
    switch (validator.validationType) {
      case ValidationTypeApi.REQUIRED:
        validators.push(required(validator.errorMessage));
        break;
      case ValidationTypeApi.PATTERN_MATCH:
        validators.push(
          pattern(validator.value as RegExp, validator.errorMessage),
        );
        break;
      case ValidationTypeApi.NOT_PATTERN_MATCH:
        validators.push(
          notPattern(validator.value as RegExp, validator.errorMessage),
        );
        break;
      case ValidationTypeApi.MIN_LENGTH:
        validators.push(
          minLength(validator.value as number, validator.errorMessage),
        );
        break;
      case ValidationTypeApi.MAX_LENGTH:
        validators.push(
          maxLength(validator.value as number, validator.errorMessage),
        );
        break;
      case ValidationTypeApi.GREATER_THAN_OR_EQUAL:
        validators.push(
          minValue(validator.value as number, validator.errorMessage),
        );
        break;
      case ValidationTypeApi.LESS_THAN_OR_EQUAL:
        validators.push(
          maxValue(validator.value as number, validator.errorMessage),
        );
        break;
      case ValidationTypeApi.DATE_FORMAT:
        validators.push(dateFormat(validator.errorMessage));
        break;
      case ValidationTypeApi.CURRENT_OR_FUTURE_DATE:
        validators.push(currentOrFutureDate(validator.errorMessage));
        break;
      default:
        break;
    }
  });
  input.control.setValidators(validators);
  input.control.updateValueAndValidity();
}

export function checkDependentQuestions(
  form: FormGroup,
  question: FormQuestion,
): void {
  if (
    question.dependentQuestions?.some(
      questionKey => !Object.keys(form.controls).includes(questionKey),
    )
  ) {
    throw new Error(
      `Value dependent questions ${question.dependentQuestions} are not correctly set for question ${question.name}`,
    );
  }
}

/**
 * Sort dropdown options helpers
 */
function getGroupedItemName(item: SelectGroups | SelectItem): string {
  return 'groupName' in item ? item.groupName : item.displayText;
}

function compareCaseInsensitive(a: string, b: string): number {
  return a.toLowerCase() > b.toLowerCase() ? 1 : -1;
}

function compareGroupedItems(
  itemA: SelectGroups | SelectItem,
  itemB: SelectGroups | SelectItem,
): number {
  return compareCaseInsensitive(
    getGroupedItemName(itemA),
    getGroupedItemName(itemB),
  );
}

function compareSelectItems(itemA: SelectItem, itemB: SelectItem): number {
  return compareCaseInsensitive(itemA.displayText, itemB.displayText);
}

function sortSelectGroupsOptions(selectGroups: SelectGroups): SelectGroups {
  return {
    ...selectGroups,
    options: sortSelectItems(selectGroups.options) as SelectItem[],
  };
}

export function sortSelectItems(
  items: readonly SelectItem[],
): readonly SelectItem[] {
  return [...items].sort((a, b) => compareSelectItems(a, b));
}

export function sortGroupedItems(
  groupedItems: readonly (SelectGroups | SelectItem)[],
): readonly (SelectGroups | SelectItem)[] {
  return groupedItems
    .map(item => ('groupName' in item ? sortSelectGroupsOptions(item) : item))
    .sort((a, b) => compareGroupedItems(a, b));
}

export function sortItems(
  items: readonly SelectAcceptedType[],
): readonly SelectAcceptedType[] {
  if (items.some(item => 'groupName' in (item as SelectGroups))) {
    return sortGroupedItems(items as readonly (SelectGroups | SelectItem)[]);
  }
  if (items.length > 0 && 'displayText' in (items[0] as SelectItem)) {
    return sortSelectItems(items as readonly SelectItem[]);
  }
  return (items as string[]).sort(compareCaseInsensitive);
}
